import { useEffect, useState } from 'react';
import cn from 'classnames';

import Image from 'components/image/Image';
import { imageSizes } from 'components/image/image-sizes';
import Heading from 'components/heading/Heading';
import Accordion from 'components/accordion/Accordion';
import AutoGrid from 'components/auto-grid/AutoGrid';
import DatesMore from 'components/dates-more/DatesMore';
import Icon from 'components/icon/Icon';

import { IProposalShortcuts } from 'source/NFR.types';

const escapeSpecialChar = (str: string) => str.split('?').join('\\?');

const ProposalShortcuts: React.FC<IProposalShortcuts> = ({ title, items, accordion, initiallyOpenUrls }) => {
  const [openOnPageLoad, setOpenOnPageLoad] = useState<boolean>(false);

  useEffect(() => {
    if (!initiallyOpenUrls) return;
    const isMatch = initiallyOpenUrls.some(url => {
      const regExp = new RegExp(`${escapeSpecialChar(url)}/?$`);
      return regExp.test(window.location.href);
    });
    setOpenOnPageLoad(isMatch);
  }, [initiallyOpenUrls]);

  if (openOnPageLoad === undefined) return null; // Because Accordions "initiallyOpen" prop only works on mount
  return (
    <Accordion {...accordion} buttonTextForLabel={title} initiallyOpen={openOnPageLoad}>
      {({ Button, Collapse, isOpen }) => (
        <div
          className={cn('proposal-shortcuts', {
            'proposal-shortcuts--closed': !isOpen
          })}
        >
          {title && (
            <Button className="proposal-shortcuts--button">
              <Heading className="proposal-shortcuts--title">{title}</Heading>
              <Icon
                className={cn('proposal-shortcuts--button-icon', {
                  'proposal-shortcuts--button-icon--is-open': isOpen
                })}
                name="small-arrow-down"
                fill={true}
              />
            </Button>
          )}
          <Collapse>
            <AutoGrid className="proposal-shortcuts--grid" minWidth={330}>
              {AutoGridItem =>
                items?.map(item => (
                  <AutoGridItem className="proposal-shortcuts--grid-item" key={item.articleTitle}>
                    {item.title && (
                      <Heading
                        className="proposal-shortcuts--grid-item-title"
                        level={2} //due to the fact that its nested inside a button, and therefore not considered by accecibility tools
                      >
                        {item.title}
                      </Heading>
                    )}
                    <div className="proposal-shortcuts--item">
                      <a className="proposal-shortcuts--item-link" href={item.link.url}>
                        {item.image && (
                          <div className="proposal-shortcuts--item-link-image">
                            <Image {...imageSizes.proposalShortcuts} {...item.image} loading="lazy" />
                          </div>
                        )}
                        <div className="proposal-shortcuts--item-link-text-area">
                          {item.articleTitle && (
                            <Heading className="proposal-shortcuts--item-link-title" level={3}>
                              {item.articleTitle}
                            </Heading>
                          )}
                          {item.text && <p className="proposal-shortcuts--item-link-text">{item.text}</p>}
                          {item.status && (
                            <div className="proposal-shortcuts--item-link-status">
                              <time dateTime={item.status}>{item.status}</time>
                            </div>
                          )}
                        </div>
                      </a>
                      {item.moreDates && (
                        <DatesMore className="proposal-shortcuts--item-dates-more" {...item.moreDates} />
                      )}
                    </div>
                  </AutoGridItem>
                ))
              }
            </AutoGrid>
          </Collapse>
        </div>
      )}
    </Accordion>
  );
};

ProposalShortcuts.displayName = 'ProposalShortcuts';

export default ProposalShortcuts;
